import { useEffect } from "react"

import { useMsal } from "@azure/msal-react"
import { AlertCircleIcon } from "lucide-react"
import { useLocation, useNavigate } from "react-router"

import { hasRole } from "@/util/hasRole.ts"

import { Alert, AlertDescription, AlertTitle } from "./ui/alert.tsx"

/**
 * AuthWrap - Role-based authorization wrapper component
 *
 * Conditionally renders children based on the user's role permissions.
 * Supports multiple authorization behaviors for different use cases.
 *
 * @example
 * // Component-level: Silent hiding (sidebar navigation)
 * <AuthWrap role="KPI.Admin">
 *   <NavButton to="/admin" label="Admin" />
 * </AuthWrap>
 *
 * @example
 * // Component-level: Inline error message
 * <AuthWrap role="Report.Admin" showMessage={true}>
 *   <ReportEditor />
 * </AuthWrap>
 *
 * @example
 * // Multiple roles (user needs ANY of these roles)
 * <AuthWrap role="KPI.Admin,Logistics.Admin" showMessage={true} redirectOnDenied={true}>
 *   <AdminPanel />
 * </AuthWrap>
 *
 * @example
 * // Route-level: Redirect to permission denied page
 * <AuthWrap role="Datapoint.Admin" showMessage={true} redirectOnDenied={true}>
 *   <DatapointManagement />
 * </AuthWrap>
 *
 * @param role - Required Entra ID role name(s). Supports single roles (e.g., "KPI.Admin")
 *   or multiple comma-separated roles (e.g., "KPI.Admin,Report.Admin"). User needs ANY of the
 *   specified roles. Users with "Full.Dashboard.Admin" role automatically pass all role checks.
 * @param showMessage - If true, shows error message when permission denied
 * @param redirectOnDenied - If true, redirects to /permission-denied page when denied
 * @param children - Components to render when user has required permissions
 *
 * Behavior matrix:
 * - No role: Hide children silently (return empty fragment)
 * - showMessage=true, redirectOnDenied=false: Show inline alert
 * - showMessage=true, redirectOnDenied=true: Navigate to permission denied page
 */
export default function AuthWrap({
  role,
  showMessage,
  redirectOnDenied = false,
  children,
}: {
  role: string
  showMessage?: boolean
  redirectOnDenied?: boolean
  children: React.ReactNode
}) {
  useMsal()
  const navigate = useNavigate()
  const location = useLocation()

  // Check if user has any of the required roles (comma-separated)
  // Uses hasRole() which automatically handles Full.Dashboard.Admin superuser role
  const requiredRoles = role.split(",").map((r) => r.trim())
  const hasRequiredRole = requiredRoles.some((requiredRole) => hasRole(requiredRole))

  useEffect(() => {
    if (!hasRequiredRole && showMessage && redirectOnDenied) {
      // Navigate to permission denied page with current location as returnTo
      navigate(`/permission-denied?returnTo=${encodeURIComponent(location.pathname)}`)
    }
  }, [hasRequiredRole, showMessage, redirectOnDenied, navigate, location.pathname])

  if (hasRequiredRole) {
    return children
  } else {
    if (showMessage) {
      if (redirectOnDenied) {
        // This will be handled by the useEffect navigation
        return null
      } else {
        // Show inline alert
        return (
          <Alert variant="destructive">
            <AlertCircleIcon />
            <AlertTitle>Unauthorized</AlertTitle>
            <AlertDescription>
              <div className="mt-1">
                You must have the <span className="font-semibold">{role}</span> role to access this
                content.
              </div>
            </AlertDescription>
          </Alert>
        )
      }
    } else {
      return <></>
    }
  }
}
